/**
 * Emit analytics events through Segment.
 *
 * Note: this is *not* isomorphic, these functions will only work in the browser.
 *
 * Technical Documentation for the global.analytics object can be found here:
 * https://segment.com/docs/sources/website/analytics.js/
 */
import type { ThunkAction } from '../types/app.types';
import { DEV, VERSION, IS_BROWSER } from '../config';
import { isDesktop, isTablet } from '../store/modules/header-module';

export type PageCategory = string; // Future: restrict to a list of valid string literals

export type PageName = string;
export type PageProperties = {
  name?: string;
  // DO NOT USE; reserved for future use in Segment
  path?: string;
  // defaults to location.pathname
  referrer?: string;
  // defaults to document.referrer
  search?: string;
  // defaults to location.search
  title?: string;
  // defaults to document.title
  url?: string; // defaults to location.href
  medId?: string;
  splashCode?: string;
  isOnboarding?: boolean;
  entryPoint?: string | string[];
  currentPriceTier?: string;
  partner?: string | null;
  // Special Properties
  [key: string]: string | number | boolean | Record<string, unknown> | Array<unknown>;
};

/**
 * See https://segment.com/docs/sources/website/analytics.js/#selecting-destinations
 * These are case sensitive
 */
export type Integrations = {
  All: boolean;
  'Amazon S3': boolean;
  'Google Analytics': boolean;
  'Facebook Pixel': boolean;
  'Google Tag Manager': boolean;
};
export type TrackOptions = {
  integrations?: Integrations;
  page?: {
    path?: string;
    url?: string;
  };
};
export type EventName = string;
export type EventProperties = {
  revenue?: number;
  // SPECIAL BEHAVIOR IN SEGMENT: must be a decimal value
  currency?: string;
  // SPECIAL BEHAVIOR IN SEGMENT: must be in ISO 4127 format
  value?: number;
  // SPECIAL BEHAVIOR IN SEGMENT: abstract "value" number
  medId?: string;
  checked?: boolean;
  accountId?: string;
  scheduleId?: string;
  // Special Properties
  [key: string]: string | number | boolean | Record<string, unknown> | Array<unknown>;
};
export type PageOptions = any;

export function reset(): void {
  global.analytics.reset();
}

/**
 * `properties` has some special behavior in Segment. See https://segment.com/docs/spec/page/#properties
 */
export function trackPage(
  category: PageCategory = 'Uncategorized',
  name: PageName | null | undefined = undefined,
  properties: PageProperties | null | undefined = undefined
): ThunkAction<Promise<void>> {
  return function (dispatch, getState): Promise<any> {
    const appState = getState();
    const injectedProperties = {
      ...properties,
      sessionId: appState.account && appState.account.sessionId,
      version: VERSION,
      device: isDesktop(appState) ? 'desktop' : isTablet(appState) ? 'tablet' : 'mobile',
    };
    global.analytics.page(category, name, injectedProperties);
    return Promise.resolve();
  };
}

// segment will create a table for each event name, so we keep a whitelist to prevent things from blowing up
const eventNameWhitelist = [
  'Clicked',
  'Viewed',
  'Input',
  'Viewed Home Page',
  'Viewed Product',
  'Created Account',
  'Added Product',
  'Completed Order',
  'Transactional Completed Order',
  'Filled',
  'Installed App',
  'Started Transfer',
  'Processed Transfer',
  'Completed Transfer',
  'Linkshare Gateway',
  'Patient Information pre-populated during checkout',
  'Linkshare Purchase',
];

/**
 * `properties` has some special behavior in Segment. See https://segment.com/docs/spec/track/#properties
 */
export function trackEvent(
  eventName: EventName,
  properties: EventProperties = {},
  options: TrackOptions | null | undefined = undefined
): ThunkAction {
  return function (dispatch, getState): void {
    if (DEV && !eventNameWhitelist.includes(eventName)) {
      console.warn(
        `${eventName} is not included in the event whitelist, acceptable values are ${eventNameWhitelist.join(',')}`
      );
    }

    const appState = getState();
    const injectedProperties = {
      ...properties,
      sessionId: appState.account && appState.account.sessionId,
      version: VERSION,
      device: isDesktop(appState) ? 'desktop' : isTablet(appState) ? 'tablet' : 'mobile',
    };
    global.analytics.track(eventName, injectedProperties, options);
  };
}

export function trackProductView({
  medId,
  medNameId,
  quantity,
  edlpAvailable,
  bsdAvailable,
  hdAvailable,
  telehAvailable,
  insuranceAvailable,
  hdRank,
  edlpRank,
  telehRank,
}: {
  medId: string;
  medNameId: string;
  quantity: number | null | undefined;
  edlpAvailable: boolean;
  bsdAvailable?: boolean;
  hdAvailable: boolean;
  telehAvailable?: boolean;
  insuranceAvailable?: boolean;
  hdRank?: number;
  edlpRank?: number;
  telehRank?: number;
}) {
  return trackEvent('Viewed Product', {
    medId,
    medNameId,
    quantity,
    edlpAvailable,
    bsdAvailable,
    hdAvailable,
    telehAvailable,
    insuranceAvailable,
    hdRank,
    edlpRank,
    telehRank,
  });
}

type TargetType =
  | 'button'
  | 'link'
  | 'select'
  | 'cell'
  | 'search'
  | 'textfield'
  | 'radiobutton'
  | 'dropdown'
  | 'menu'
  | 'scroll'
  | 'tab'
  | 'modal'
  | 'alert'
  | 'toggle'
  | 'submit'
  | 'inputfield';

export function trackClick(targetName: string, targetType: TargetType, extras: EventProperties = {}): ThunkAction {
  return trackEvent('Clicked', {
    targetName,
    targetType,
    ...extras,
  });
}

export function trackView(targetName: string, extras: EventProperties = {}): ThunkAction {
  return trackEvent('Viewed', {
    targetName,
    ...extras,
  });
}

export function trackInput(
  targetName: string,
  targetType: TargetType,
  inputText: string,
  extras: EventProperties = {}
): ThunkAction {
  return trackEvent('Input', {
    targetName,
    targetType: targetType ? targetType : 'inputfield',
    inputText,
    ...extras,
  });
}

type UserTraits = {
  address: {
    city?: string;
    country?: string;
    postalCode?: string;
    state?: string;
    street?: string;
  };
  age?: number;
  createdAt?: string;
  // Segment recommend ISO-8601 date strings, but also accept Unix timestamps for convenience.
  gender?: string;
  id: string;
};

export function identify(userId: string, traits?: UserTraits): void {
  IS_BROWSER && global.analytics.identify(userId, traits);
}

// simple wrappers around tracking events that add `category: Reverie`
export const trackReverieEvent = (
  eventName: string,
  eventData: Record<string, any> = {},
  options: Record<string, any> = {}
) => {
  return trackEvent(eventName, { ...eventData, category: 'Reverie' }, options);
};
export const trackReverieClick = (targetName: string, targetType: TargetType, extras: EventProperties = {}) => {
  return trackEvent('Clicked', {
    targetName,
    targetType,
    category: 'Reverie',
    ...extras,
  });
};
